import { Button, Icons, Input, Label, SingleCombobox } from '@commenda/kakashi';
import { WorkflowRuntimeStepStatus, WorkflowStepType } from '@commenda/types';
import { ExpressionType } from '@commenda/types/src/workflow/dsl/enums';
import { useReactFlow } from '@xyflow/react';
import { useRef, useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { getEdgeConditionSuggestions } from './edgeConditionSuggestions';

export const EdgePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { deleteElements } = useReactFlow();
  const { workflow, setSelectedEntities, setWorkflow } = useWorkflow();

  const getSuggestions = (input: string): string[] => {
    return getEdgeConditionSuggestions(input, workflow?.steps ?? []);
  };

  const selectedStepTransition = workflow?.stepTransitions.find(
    (transition) => transition.id === selectedEntity.id
  );
  const sourceStep = workflow?.steps.find(
    (step) => step.id === selectedStepTransition?.fromStepId
  );

  const isConditionApplicable =
    sourceStep?.type === WorkflowStepType.GATEWAY_BOOLEAN_EXCLUSIVE_SPLIT ||
    sourceStep?.type === WorkflowStepType.GATEWAY_INCLUSIVE_SPLIT;

  const conditionInputRef = useRef<HTMLInputElement>(null);

  const [inputSuggestions, setInputSuggestions] = useState<string[]>(
    getSuggestions(selectedStepTransition?.condition?.src || '')
  );

  const addSuggestionToCondition = (condition: string, suggestion: string) => {
    const allSegments = condition.split('.');
    allSegments.pop();
    allSegments.push(suggestion);
    return allSegments.join('.');
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <span className="txt-medium-medium">Properties</span>
        <Button
          variant="tertiary"
          size="sm"
          onClick={() => {
            deleteElements({
              edges: selectedStepTransition ? [{ id: selectedEntity.id }] : [],
            });
            setSelectedEntities([]);
          }}
        >
          <Icons.Trash01 />
        </Button>
      </div>

      <div className="flex gap-2 flex-col">
        <Label>Label</Label>
        <Input
          value={selectedStepTransition?.label || ''}
          onChange={(e) => {
            setWorkflow({
              ...workflow,
              stepTransitions: workflow?.stepTransitions.map((transition) =>
                transition.id === selectedEntity.id
                  ? { ...transition, label: e.target.value }
                  : transition
              ),
            });
          }}
        />
      </div>
      {isConditionApplicable && (
        <div className="flex gap-4 flex-col">
          <div className="flex gap-2 flex-col">
            <Label>Condition</Label>
            <Input
              value={selectedStepTransition?.condition?.src || ''}
              ref={conditionInputRef}
              onChange={(e) => {
                setWorkflow({
                  ...workflow,
                  stepTransitions: workflow?.stepTransitions.map(
                    (transition) =>
                      transition.id === selectedEntity.id
                        ? {
                            ...transition,
                            condition: {
                              src: e.target.value,
                              type: ExpressionType.JSONATA,
                            },
                          }
                        : transition
                  ),
                });
                const allSuggestions = getSuggestions(e.target.value);
                setInputSuggestions(allSuggestions);
              }}
            />
            {inputSuggestions.length ? (
              <div className="flex gap-2 flex-wrap">
                {inputSuggestions.map((suggestion) => (
                  <div
                    key={suggestion}
                    className="py-2 px-3 cursor-pointer rounded-full border border-border-primary bg-bg-secondary hover:bg-bg-secondary-hover txt-extra-small-medium"
                    onClick={() => {
                      setWorkflow({
                        ...workflow,
                        stepTransitions: workflow?.stepTransitions.map(
                          (transition) =>
                            transition.id === selectedEntity.id
                              ? {
                                  ...transition,
                                  condition: {
                                    src: addSuggestionToCondition(
                                      transition.condition?.src || '',
                                      suggestion
                                    ),
                                    type: ExpressionType.JSONATA,
                                  },
                                }
                              : transition
                        ),
                      });
                      setInputSuggestions([]);
                      if (conditionInputRef.current) {
                        conditionInputRef.current.focus();
                      }
                    }}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex gap-2 flex-col">
            <h4 className="txt-small-bold">On Arrival</h4>
            <div className="flex gap-2 flex-col">
              <Label>Status of the arrival step</Label>
              <SingleCombobox
                options={Object.values(WorkflowRuntimeStepStatus).map(
                  (status) => ({ label: status, value: status })
                )}
                value={selectedStepTransition?.onArrival?.status || null}
                setValue={(selectedStatus) =>
                  setWorkflow({
                    ...workflow,
                    stepTransitions: workflow?.stepTransitions.map(
                      (transition) =>
                        transition.id === selectedEntity.id
                          ? {
                              ...transition,
                              onArrival: {
                                ...(transition.onArrival || {}),
                                status:
                                  selectedStatus as WorkflowRuntimeStepStatus,
                              },
                            }
                          : transition
                    ),
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
